import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { antennasActions } from 'state/antennas';
import { switchesActions } from 'state/switches';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Schedule } from 'models/schedules';
import { AntennaManager } from 'models';

import ScheduleDetail from 'components/schedules/Detailed';
import Switch from 'components/switches/Switch';
import AntennaOverview from 'components/antennas/Overview';
import TLEDetails from 'components/schedules/TLEDetails';

interface Props {
    schedule: Schedule;
}

/**
 * How long before a pass and after a pass that the controls will be active
 */
const CONTROL_GRACE_PERIOD = 15 * 60 * 1000;

const CurrentPassPanel: React.FunctionComponent<Props> = ({ schedule }) => {
    const theme = useTheme();

    const dispatch = useAppDispatch();

    const { switches, antennas } = useAppSelector((root) => root);

    const [isRefreshing, setIsRefreshing] = useState(false);

    const antenna = useMemo<AntennaManager.Antenna | null>(
        () =>
            antennas.list.object?.find(
                (ant) => schedule.antenna.id == ant.id,
            ) ?? null,
        [antennas, schedule],
    );

    const passingNow = useMemo(
        () =>
            schedule.start_time.getTime() - CONTROL_GRACE_PERIOD < Date.now() &&
            schedule.stop_time.getTime() + CONTROL_GRACE_PERIOD > Date.now(),
        [schedule, isRefreshing],
    );

    const getAntenna = async () => {
        if (isRefreshing) return;

        setIsRefreshing(true);
        try {
            await dispatch(antennasActions.getAntennaList());
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        getAntenna();
    }, [schedule]);

    useEffect(() => {
        if (schedule.antenna.groundstation)
            dispatch(
                switchesActions.getSwitchList({
                    groundStationId: schedule.antenna.groundstation.id,
                }),
            );
    }, [schedule]);

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                [theme.breakpoints.down('xl')]: {
                    gridTemplateRows: 'auto auto',
                },
                [theme.breakpoints.up('xl')]: {
                    gridTemplateColumns: '1fr 1fr',
                },
            }}
        >
            <ScheduleDetail context="satellite" schedule={schedule} />

            {antenna && (
                <Paper sx={{ p: 1 }}>
                    <AntennaOverview
                        antenna={antenna}
                        onRefreshClick={() => getAntenna()}
                        isRefreshing={isRefreshing}
                    />
                </Paper>
            )}
            {!antenna && (
                <Paper sx={{ p: 1 }}>
                    <AntennaOverview
                        antenna={schedule.antenna}
                        onRefreshClick={() => getAntenna()}
                        isRefreshing={isRefreshing}
                        hideRefresh
                    />
                </Paper>
            )}
            <TLEDetails schedule={schedule} />
            {!passingNow && (
                <Paper sx={{ p: 1 }}>
                    <Typography variant="h6">
                        Controls will be activated during the pass.
                    </Typography>
                </Paper>
            )}
            {passingNow && (
                <Paper sx={{ p: 1, display: 'grid', gap: 1 }}>
                    {switches.listByGroundStation[
                        schedule.antenna.groundstation.id
                    ]?.object
                        ?.filter((obj) => obj.satelliteOperatorAllowed)
                        .map((obj) => (
                            <Switch key={obj.switchId} obj={obj} />
                        )) ?? (
                        <Typography>
                            No Switches Available for this Ground Station
                        </Typography>
                    )}
                </Paper>
            )}
        </Box>
    );
};

CurrentPassPanel.displayName = 'CurrentPassPanel';
export default CurrentPassPanel;
